import { getNewSheetModeWorksheetName, NEW_SHEET_MODE_DATE_TIME_FORMAT } from '@shared/excel/excel';
import { RangeAddress } from '@shared/excel/range-address';
import { PULL_FLOWSTEP_ADDITIONAL_HEADERS } from '@shared/excel/runtime/flowstepExecution';
import {
  ExecutorActionError,
  ExecutorActionEvent,
  ExecutorActionFinished,
  ExecutorActionInfo,
  ExecutorActionStarted,
  ExecutorCancelled,
  ExecutorNewWorksheetCreated,
} from '@shared/execution/executors/events';
import { AddressResolver, AddressResolverFactory, CellFormatter, ValueConverter } from '@shared/execution/types';
import { InsertBehaviour, WorkbookApi, WorkbookApiFactory } from '@shared/execution/workbook-api';
import { EventBus } from '@shared/infrastructure/app-event-bus';
import { ProviderClient, ProviderFactory } from '@shared/providers/provider';
import { ProviderMeta } from '@shared/providers/provider-meta';
import { isMetadata, MetadataResult, STREAM_METADATA } from '@shared/providers/provider-transport';
import { DataField } from '@shared/providers/types';
import { Flowstep, ProviderOptions } from '@shared/types/flowstep';
import { CancelError, CancelToken } from '@shared/utils/cancelToken';
import { NoDataError, TruncateDataError } from '@shared/utils/flowstep';
import dayjs from 'dayjs';
import { CellAddressPickerValue } from 'frontend/excel365-addin/src/v2/components/base/CellAddressPicker/types';
import Highland from 'highland';
import { PullMode } from 'shared/types/flowstep';
import Executor from '.';
import { ValueConverterFactory } from '../workbook-api/converters';
import { CellFormatterFactory } from '../workbook-api/formatters';
void Highland;

interface InjectedDependencies {
  providerFactory: ProviderFactory;
  valueConverterFactory: ValueConverterFactory;
  workbookApiFactory: WorkbookApiFactory;
  eventBus: EventBus;
  addressResolverFactory: AddressResolverFactory;
  cellFormatterFactory: CellFormatterFactory;
}

interface WritingContext {
  usedFields: DataField[];
  rowStream: Highland.Stream<any>;
  rowNumberFormats: any;
  insertColumnsCount?: number;
  cancelToken?: CancelToken;
}

class PullExecutor extends Executor {
  private providerFactory: ProviderFactory;
  private valueConverterFactory: ValueConverterFactory;
  private readonly workbookApiFactory: WorkbookApiFactory;
  private eventBus: EventBus;
  private addressResolverFactory: AddressResolverFactory;
  private cellFormatterFactory: CellFormatterFactory;

  constructor(deps: InjectedDependencies) {
    super();
    this.providerFactory = deps.providerFactory;
    this.valueConverterFactory = deps.valueConverterFactory;
    this.workbookApiFactory = deps.workbookApiFactory;
    this.eventBus = deps.eventBus;
    this.addressResolverFactory = deps.addressResolverFactory;
    this.cellFormatterFactory = deps.cellFormatterFactory;
  }

  private timestamp: string | null = null;
  private receivedRowsCount = 0;
  private warnings: Error[] = [];
  flowstep!: Flowstep<ProviderOptions>;
  worksheet: any;
  private workbookApi!: WorkbookApi<any>;
  private addressResolver!: AddressResolver;
  private valueConverter!: ValueConverter;
  private cellFormatter: CellFormatter | null = null;
  private provider!: ProviderClient;
  private providerMeta!: ProviderMeta;
  private timezone!: string;

  init(flowstep: Flowstep, context: any): void {
    this.flowstep = flowstep as Flowstep<ProviderOptions>;
    this.timezone = context.timezone;
    this.workbookApi = this.workbookApiFactory(context);
    this.addressResolver = this.addressResolverFactory.getAddressResolver(flowstep, context);
    this.valueConverter = this.valueConverterFactory.getValueConverter(flowstep.provider);
    this.cellFormatter = this.cellFormatterFactory.getCellFormatter(flowstep.provider);
    this.provider = this.providerFactory.getProviderClient(flowstep.provider, context);
    this.providerMeta = this.providerFactory.getProviderMeta(flowstep.provider);
  }

  async execute(cancelToken?: CancelToken) {
    let targetWorksheetId!: string;
    let protectionOptions: any = null;
    try {
      this.eventBus.notify(new ExecutorActionStarted());
      this.flowstep = (await this.addressResolver.replaceAddressesByValues(this.flowstep)) as Flowstep<ProviderOptions>;

      const {
        pullMode,
        worksheetId,
        target: topLeftCellAddress,
        withDateTimeColumn,
        createDataTable,
        useDropdownsForPicklists,
        protectionPassword,
      } = this.flowstep.options;

      targetWorksheetId = worksheetId;

      if (pullMode === PullMode.NEW_SHEET) {
        const dateTime = dayjs().format(NEW_SHEET_MODE_DATE_TIME_FORMAT);
        const worksheetName = getNewSheetModeWorksheetName(this.flowstep.name, dateTime);
        this.eventBus.notify(new ExecutorActionInfo(`Creating new worksheet ${worksheetName}`));
        targetWorksheetId = await this.workbookApi.createNewWorksheet(worksheetName);
        this.eventBus.notify(new ExecutorNewWorksheetCreated(worksheetName));
      }

      //For schedeuler backward compatibility
      this.worksheet = await this.workbookApi.getWorksheet?.(targetWorksheetId);

      protectionOptions = await this.workbookApi.disableProtection?.(targetWorksheetId, protectionPassword);

      const filters = await this.workbookApi.clearFilters(targetWorksheetId);

      const fields = await this.provider.loadObjectFields(this.flowstep.providerOptions.objectType);
      let usedFields = await this.getUsedFields(fields);
      await this.addressResolver.resolveConditionValues(this.flowstep, fields);

      let providerStream = await this.provider.getFlowstepData(this.flowstep, cancelToken);

      if (usedFields.length === 0) {
        const { readedFields, mainStream } = await this.readUsedFields(providerStream);
        usedFields = readedFields;
        providerStream = mainStream;
      }

      this.eventBus.notify(new ExecutorActionEvent('Detecting the target range'));

      const trimToCell = pullMode === PullMode.APPEND || !createDataTable;
      const detectedRange = await this.workbookApi.detectRangeByCell(targetWorksheetId, topLeftCellAddress, { trimToCell });

      const detectedHeaders = detectedRange ? await this.workbookApi.getColumnHeaders(detectedRange) : null;

      const isDateTimeColumnEnabled = pullMode === PullMode.APPEND && withDateTimeColumn;
      const needToAddDateTimeColumn =
        isDateTimeColumnEnabled && !detectedHeaders?.includes(PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime);

      if (isDateTimeColumnEnabled) {
        usedFields.push({
          name: PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime,
          label: PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime,
          type: 'datetime',
        } as DataField);
      }

      let targetRange = null;
      let insertColumnsCount = undefined;
      if (!detectedHeaders) {
        targetRange = await this.workbookApi.getRangeBySize(
          targetWorksheetId,
          topLeftCellAddress,
          1,
          usedFields.length,
        );

        if (pullMode !== PullMode.NEW_SHEET) {
          targetRange = await this.workbookApi.insertColumnsAt(targetRange);
        }
      } else {
        const extraColumnsCount = Math.max(usedFields.length - detectedHeaders.length, Number(needToAddDateTimeColumn));
        targetRange = detectedRange;
        insertColumnsCount = extraColumnsCount;
        if (pullMode === PullMode.APPEND && extraColumnsCount > 0) {
          targetRange = await this.workbookApi.insertColumnsAfter(detectedRange, extraColumnsCount);
        }
      }

      this.eventBus.notify(new ExecutorActionEvent('Receiving data'));
      const rowStream = this.toRowStream(providerStream, usedFields, isDateTimeColumnEnabled);

      const ctx: WritingContext = {
        usedFields,
        rowStream,
        insertColumnsCount,
        rowNumberFormats: this.cellFormatter?.getRowNumberFormats(usedFields),
        cancelToken,
      };

      let tableRange;

      if (pullMode === PullMode.APPEND && detectedRange && detectedHeaders) {
        tableRange = await this.writeAppend(ctx, targetRange, detectedHeaders, needToAddDateTimeColumn);
      } else {
        tableRange = await this.writeOverwrite(ctx, targetRange, targetWorksheetId);
      }

      if (filters?.length) {
        await this.workbookApi.applyFilters(targetWorksheetId, filters);
      }

      if (createDataTable) {
        await this.workbookApi.createTable(tableRange, { resizeExistent: true });
      }

      if (pullMode !== PullMode.APPEND && !detectedRange) {
        this.workbookApi.autofitColumns(tableRange);
      }

      if (useDropdownsForPicklists) {
        await this.workbookApi.applyPickLists(tableRange, this.flowstep.id, ctx.usedFields);
      }

      const result = { resultSuccessRange: tableRange, warnings: this.warnings };

      this.eventBus.notify(new ExecutorActionFinished('Execution finished', result));

      return result;
    } catch (error: unknown) {
      console.error('Error during Pull execution', error);
      if (error instanceof NoDataError) {
        this.eventBus.notify(new ExecutorActionFinished('Execution finished with warnings', { warnings: [error] }));
        return { warnings: [error] };
      }
      if (error instanceof CancelError) {
        this.eventBus.notify(new ExecutorCancelled(error.message));
        return { error };
      }
      this.eventBus.notify(new ExecutorActionError(error as Error));
      return { warnings: [], error };
    } finally {
      if (this.flowstep.options.timestampCell) {
        await this.setLastRunTimestamp();
      }
      if (protectionOptions && targetWorksheetId) {
        await this.workbookApi.enableProtection?.(
          targetWorksheetId,
          protectionOptions,
          this.flowstep.options.protectionPassword,
        );
      }
    }
  }

  private async readUsedFields(providerSteam: Highland.Stream<any>) {
    const peekStream = providerSteam.observe();
    const mainStream = providerSteam.observe();
    providerSteam.resume();

    const firstRow = await new Promise<any>((resolve, reject) => {
      peekStream
        .filter((item: any) => !isMetadata(item))
        .take(1)
        .errors((err: Error) => reject(err))
        .each((item: any) => resolve(item));
    });

    return {
      readedFields: firstRow.map((header: any) => this.providerMeta.getDataFieldFromHeader(String(header))),
      mainStream,
    };
  }

  private async writeAppend(
    ctx: WritingContext,
    rangeToAppend: any,
    detectedHeaders: string[],
    needToAddDateTimeColumn: boolean,
  ) {
    if (ctx.usedFields.length > detectedHeaders.length + Number(needToAddDateTimeColumn)) {
      throw new Error(
        `Column count in 'Append' mode mismatch.` +
          `Expected: <= ${ctx.usedFields.length}, observed: ${detectedHeaders.length}`,
      );
    }

    if (needToAddDateTimeColumn) {
      const tableRangeInfo = await this.workbookApi.getRangeAddressInfo(rangeToAppend);
      const dateTimeCellAddress = RangeAddress.fromString(tableRangeInfo.address).getCell(0, detectedHeaders.length);
      const dateTimeCellRange = await this.workbookApi.getRangeByAddress(dateTimeCellAddress.addressWithSheet);
      await this.workbookApi.writeRangeValue(dateTimeCellRange, [[PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime]]);
    }

    const appendedRowCount = await this.workbookApi.appendRangeData(rangeToAppend, ctx.rowStream.drop(1), {
      insertRowsMode: InsertBehaviour.always,
      insertColumnCount: ctx.insertColumnsCount,
      rowNumberFormats: ctx.rowNumberFormats,
    });

    if (ctx.cancelToken?.isCanceled()) {
      const reason = ctx.cancelToken.reason || 'Execution cancelled';
      throw new CancelError(reason);
    }

    return this.workbookApi.addRows(rangeToAppend, appendedRowCount);
  }

  private async writeOverwrite(ctx: WritingContext, targetRange: any, targetWorksheetId: string) {
    const { target: topLeftCellAddress } = this.flowstep.options;

    const rowCount = await this.workbookApi.writeRangeData(targetRange, ctx.rowStream, {
      insertRowsMode: InsertBehaviour.afterEnd,
      insertColumnCount: ctx.insertColumnsCount,
      rowNumberFormats: ctx.rowNumberFormats,
    });

    if (ctx.cancelToken?.isCanceled()) {
      const reason = ctx.cancelToken.reason || 'Execution cancelled';
      throw new CancelError(reason);
    }

    try {
      await this.workbookApi.truncateRangeRows(targetRange, rowCount);
    } catch (error) {
      console.log('PullExecutor: topLeftCellAddress', topLeftCellAddress);
      console.log('PullExecutor: targetRange', targetRange.rangeAddress?.address, targetRange.rangeAddress?.rowCount);
      console.log('PullExecutor: rows written', rowCount);
      console.log('PullExecutor: inserted columns count', ctx.insertColumnsCount);
      throw error;
    }

    const tableRange = await this.workbookApi.getRangeBySize(
      targetWorksheetId,
      topLeftCellAddress,
      rowCount,
      ctx.usedFields.length,
    );

    if (!tableRange) {
      throw new NoDataError('Could not detect the range of data after the Pull operation');
    }

    return tableRange;
  }

  private toRowStream(providerStream: Highland.Stream<any>, fields: DataField[], withDateTimeColumn: boolean) {
    let isHeaderRow = true;
    let isTruncated = false;

    return providerStream
      .filter((item: any) => {
        if (isMetadata(item)) {
          this.processMetaItem(item);
          return false;
        }
        return true;
      })
      .map((rowValues) => {
        if (isHeaderRow) {
          isHeaderRow = false;
          const result = this.convertHeaderValues(rowValues, fields);
          withDateTimeColumn && result.push(PULL_FLOWSTEP_ADDITIONAL_HEADERS.AppendDateTime);
          return result;
        }
        const result = this.convertRowValues(rowValues, fields);
        withDateTimeColumn && result.push(this.timestamp);
        return result;
      })
      .map((row: any[]) => {
        if (!Array.isArray(row)) return row;
        return row.map((value) => {
          if (value?.length > 32767) {
            if (!isTruncated) {
              isTruncated = true;
              this.warnings.push(new TruncateDataError());
            }
            return value.substring(0, 32767);
          }
          return value;
        });
      })
      .tap(() => {
        this.receivedRowsCount++;
        if (this.receivedRowsCount % 1000 === 0) {
          this.eventBus.notify(new ExecutorActionEvent('Receiving data', `${this.receivedRowsCount} rows`));
        }
      });
  }

  private convertHeaderValues(headerValues: any, usedFields: DataField[]) {
    if (!Array.isArray(headerValues)) {
      return headerValues;
    }

    return headerValues.map((_, index) => usedFields[index].label);
  }

  private convertRowValues(rowValues: any[], fields: DataField[]) {
    if (!Array.isArray(rowValues)) {
      return rowValues;
    }

    return rowValues.map((value, index) =>
      this.valueConverter.toExcelValue(value, fields[index], { timezone: this.timezone }),
    );
  }

  private processMetaItem(item: MetadataResult): void {
    if (item.type === STREAM_METADATA.TIMESTAMP) {
      this.timestamp = item.payload.timestamp;
    }
  }

  private async setLastRunTimestamp() {
    if (!this.timestamp) {
      return;
    }

    const { options } = this.flowstep;
    let range: any;

    if (options.timestampCell?.customRange) {
      range = await this.workbookApi.getRangeByAddress(options.timestampCell.customRange);
    } else {
      const { worksheetId, address } = options.timestampCell as CellAddressPickerValue;
      range = await this.workbookApi.getRangeByAddress(`${worksheetId}!${address}`);
    }

    if (!range) {
      return;
    }
    await this.workbookApi.writeRangeValue(range, [[this.timestamp]]);
  }

  private async getUsedFields(fields: DataField[]): Promise<DataField[]> {
    if (fields.length === 0) {
      return [];
    }
    const { objectProps, excludedProps } = this.flowstep.providerOptions;

    if (excludedProps !== undefined) {
      return fields.filter((field) => !excludedProps.includes(field.name));
    }

    return objectProps
      .map((propName: string) => fields.find((field) => field.name === propName))
      .filter((field?: DataField) => !!field);
  }
}

export default PullExecutor;
